'use client'

import { motion } from 'framer-motion'
import { Shield, Users, Zap, ShoppingCart, Globe, Smartphone, Clock, Building, Target, BookOpen, ArrowRight, Layers } from 'lucide-react'
import Link from 'next/link'

export default function Solutions() {
  const solutions = [
    { icon: <Shield className="w-6 h-6" />, title: 'Hospital Management', description: 'Complete healthcare platform with patient records & HIPAA compliance.', color: 'from-red-400 to-pink-500', href: '/solutions/hospital-management' },
    { icon: <BookOpen className="w-6 h-6" />, title: 'School Management', description: 'K-12 solution with student info, grades, and parent portal.', color: 'from-blue-400 to-indigo-500', href: '/solutions/school-management' },
    { icon: <Users className="w-6 h-6" />, title: 'College Management', description: 'Multi-campus higher education with exams & admissions.', color: 'from-purple-400 to-indigo-500', href: '/solutions/college-management' },
    { icon: <Zap className="w-6 h-6" />, title: 'Gym Management', description: 'Member tracking, trainer scheduling & mobile app.', color: 'from-orange-400 to-red-500', href: '/solutions/gym-management' },
    { icon: <Users className="w-6 h-6" />, title: 'Marriage Bureau', description: 'AI-powered matching with secure communication.', color: 'from-pink-400 to-rose-500', href: '/solutions/marriage-bureau' },
    { icon: <Globe className="w-6 h-6" />, title: 'LMS Platform', description: 'E-learning with courses, assessments & progress tracking.', color: 'from-green-400 to-teal-500', href: '/solutions/lms' },
    { icon: <ShoppingCart className="w-6 h-6" />, title: 'Ecommerce Platform', description: 'Multi-vendor store with payments & inventory.', color: 'from-emerald-400 to-green-500', href: '/solutions/ecommerce-platform' },
    { icon: <Smartphone className="w-6 h-6" />, title: 'Cab Service', description: 'Ride-hailing with GPS, driver apps & fare calculation.', color: 'from-yellow-400 to-orange-500', href: '/solutions/cab-service' },
    { icon: <Clock className="w-6 h-6" />, title: 'On-Demand Services', description: 'Service marketplace with booking & provider network.', color: 'from-cyan-400 to-blue-500', href: '/solutions/on-demand-services' },
    { icon: <Building className="w-6 h-6" />, title: 'Real Estate CRM', description: 'Property management with lead tracking & listings.', color: 'from-slate-400 to-slate-600', href: '/solutions/real-estate-crm' },
    { icon: <Target className="w-6 h-6" />, title: 'CRM System', description: 'Sales pipeline, lead management & analytics.', color: 'from-indigo-400 to-purple-500', href: '/solutions/crm-system' },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  }

  return (
    <section id="solutions" className="py-20 sm:py-28 bg-gradient-to-b from-white to-sky-50/50 relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-blue-100/20 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-sm font-medium mb-4"
          >
            <Layers className="w-4 h-4" />
            Industry Solutions
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Ready-Made <span className="gradient-text">Solutions</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Deploy-ready industry-specific platforms designed for immediate implementation
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5"
        >
          {solutions.map((solution, index) => (
            <motion.div key={index} variants={itemVariants}>
              <Link
                href={solution.href}
                className="group block bg-white rounded-2xl p-5 shadow-sm border border-sky-100/50 hover:shadow-xl hover:shadow-sky-100/50 hover:border-sky-200 hover:-translate-y-2 transition-all duration-500 h-full"
              >
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${solution.color} text-white mb-4 group-hover:scale-110 group-hover:shadow-lg transition-all duration-300`}>
                  {solution.icon}
                </div>
                <h3 className="font-bold text-slate-900 mb-2 group-hover:text-sky-600 transition-colors text-sm">{solution.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed mb-3">{solution.description}</p>
                <div className="flex items-center text-sky-500 text-xs font-medium group-hover:translate-x-1 transition-transform duration-300">
                  Learn More <ArrowRight className="ml-1 w-3.5 h-3.5" />
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Why Choose Us */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 lg:p-10 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(56,189,248,0.1),transparent)] pointer-events-none" />
            <div className="text-center mb-8 relative z-10">
              <h3 className="text-2xl font-bold mb-2">Why Choose Our Solutions?</h3>
              <p className="text-slate-400 max-w-xl mx-auto text-sm">Pre-built, tested, and ready-to-deploy platforms</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
              {[
                { title: 'Fast Deployment', desc: 'Ready within 2-4 weeks', color: 'text-sky-400' },
                { title: 'Cost Effective', desc: 'Lower development costs', color: 'text-green-400' },
                { title: 'Industry Specific', desc: 'Domain expertise built-in', color: 'text-purple-400' },
                { title: 'Full Support', desc: '24/7 technical support', color: 'text-orange-400' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <h4 className={`font-semibold mb-1 ${item.color}`}>{item.title}</h4>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
              Start Your Solution <ArrowRight className="w-4 h-4" />
            </Link>
            <Link href="/contact" className="btn-secondary inline-flex items-center justify-center">
              Schedule Consultation
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
